import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, ChevronRight } from 'lucide-react';
import { collection, query, orderBy, onSnapshot, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import BBALoader from '../../components/BBALoader';
import ContactWithLogo from '../../components/crm/ContactWithLogo';

// Service lines for the Marketing Fulfillment card — the same tabs, in the
// same order, as the CRM marketing board (/crm/fulfillment/marketing). Each
// buckets marketingRequests by its stored `category`; the Website Plugins tab
// lives in a separate collection and is left to the board.
const CATEGORIES = [
  {
    key: 'website',
    label: 'Website',
    emoji: '🌐',
    ring: 'ring-sky-500',
    tile: 'bg-sky-50 dark:bg-sky-900/30',
    text: 'text-sky-600 dark:text-sky-400',
    dot: 'bg-sky-500',
    empty: 'No website clients right now.',
  },
  {
    key: 'social',
    label: 'Social Media',
    emoji: '📱',
    ring: 'ring-purple-500',
    tile: 'bg-purple-50 dark:bg-purple-900/30',
    text: 'text-purple-600 dark:text-purple-400',
    dot: 'bg-purple-500',
    empty: 'No social media clients right now.',
  },
  {
    key: 'ads',
    label: 'Ads',
    emoji: '🎯',
    ring: 'ring-orange-500',
    tile: 'bg-orange-50 dark:bg-orange-900/30',
    text: 'text-orange-600 dark:text-orange-400',
    dot: 'bg-orange-500',
    empty: 'No ads clients right now.',
  },
  {
    key: 'newsletter',
    label: 'Newsletter',
    emoji: '📰',
    ring: 'ring-amber-500',
    tile: 'bg-amber-50 dark:bg-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
    dot: 'bg-amber-500',
    empty: 'No newsletter clients right now.',
  },
  {
    key: 'reviews',
    label: 'Reviews',
    emoji: '⭐',
    ring: 'ring-emerald-500',
    tile: 'bg-emerald-50 dark:bg-emerald-900/30',
    text: 'text-emerald-600 dark:text-emerald-400',
    dot: 'bg-emerald-500',
    empty: 'No Google My Business clients right now.',
  },
];

// Board status vocabulary → label. Website calls the done state "Live"
// (matching its Kanban column); the other service lines call it "Active".
const statusLabel = (status, category) => {
  switch (status) {
    case 'requested':
    case 'pending':
      return 'Requested';
    case 'in-progress':
      return 'In Progress';
    case 'active':
    case 'completed':
      return category === 'website' ? 'Live' : 'Active';
    default:
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Not Started';
  }
};

// Open work floats to the top of each service line: requests waiting on the
// team, then in-flight, then live/active.
const STATUS_RANK = { requested: 0, pending: 0, 'in-progress': 1, active: 2, completed: 2 };

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatShortDate = (value) => {
  const d = toDate(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
};

const contactName = (account) =>
  `${account?.contact?.firstName || ''} ${account?.contact?.lastName || ''}`.trim();

/**
 * Marketing Fulfillment — the Marketing team's card on the main CRM Dashboard.
 * Sits in the same row as the rotating gallery (where Owner / Executive users
 * see the Leads & Clients chart and the HR team sees Hiring Fulfillment). Five
 * service-line tiles filter a visual grid of clients, each led by the main
 * contact's photo and the company logo, and click through to that client's
 * fulfillment page on the marketing board.
 */
const MarketingFulfillmentWidgets = ({ className = '', isMobile = false }) => {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [category, setCategory] = useState('website');

  // Client accounts (for the headshot / logo and to resolve requests that only
  // carry a company name or email).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'accounts'), where('type', '==', 'Clients')));
        if (cancelled) return;
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => c.company)
          .sort((a, b) => String(a.company).localeCompare(String(b.company)));
        setClients(list);
      } catch (err) {
        console.error('Error loading clients:', err);
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live marketing requests.
  useEffect(() => {
    const q = query(collection(db, 'marketingRequests'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setRequests(snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, status: data.status || 'pending', category: data.category || 'website' };
        }));
        setRequestsLoading(false);
      },
      (err) => {
        console.error('Error fetching marketing requests:', err);
        setRequests([]);
        setRequestsLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const clientsById = useMemo(() => {
    const map = {};
    clients.forEach((c) => { map[c.id] = c; });
    return map;
  }, [clients]);

  const clientsByCompany = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      if (c.company) map[String(c.company).toLowerCase().trim()] = c;
    });
    return map;
  }, [clients]);

  const clientsByEmail = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      const email = (c.contact?.email || c.email || '').toLowerCase().trim();
      if (email) map[email] = c;
    });
    return map;
  }, [clients]);

  const resolveAccount = useCallback((request) => {
    if (request.accountId && clientsById[request.accountId]) return clientsById[request.accountId];
    const name = (request.companyName || request.company || '').toLowerCase().trim();
    if (name && clientsByCompany[name]) return clientsByCompany[name];
    const email = (request.userEmail || '').toLowerCase().trim();
    return email ? clientsByEmail[email] || null : null;
  }, [clientsById, clientsByCompany, clientsByEmail]);

  // Bucket requests into the five service lines as uniform tiles.
  const tilesByCategory = useMemo(() => {
    const buckets = {};
    CATEGORIES.forEach((c) => { buckets[c.key] = []; });

    requests.forEach((request) => {
      if (!buckets[request.category]) return;
      const account = resolveAccount(request);
      const company = account?.company || request.companyName || request.company || 'Unknown Company';
      const accountId = account?.id || request.accountId || null;
      const dateLabel = formatShortDate(request.updatedAt || request.createdAt);
      const label = statusLabel(request.status, request.category);
      buckets[request.category].push({
        id: request.id,
        account,
        company,
        subtitle: contactName(account) || request.requestedByName || '',
        meta: dateLabel ? `${label} · ${dateLabel}` : label,
        rank: STATUS_RANK[request.status] ?? 3,
        onClick: () => (accountId
          ? navigate(`/crm/fulfillment/marketing/${request.category}/${accountId}`)
          : navigate('/crm/fulfillment/marketing', { state: { activeTab: request.category } })),
      });
    });

    // Stable sort: open work first, then the board's createdAt-desc order.
    Object.values(buckets).forEach((list) => list.sort((a, b) => a.rank - b.rank));
    return buckets;
  }, [requests, resolveAccount, navigate]);

  const activeCategory = CATEGORIES.find((c) => c.key === category) || CATEGORIES[0];
  const tiles = tilesByCategory[category] || [];
  const loading = requestsLoading || clientsLoading;

  return (
    <div className={`bg-white dark:bg-gray-900 p-6 rounded-lg shadow-sm hover-lift flex flex-col ${className}`}>
      <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-semibold text-center dark:text-white mb-5`}>
        Marketing Fulfillment
      </h2>

      {/* Service-line tiles */}
      <div className={`grid gap-3 mb-5 ${isMobile ? 'grid-cols-2' : 'grid-cols-5'}`}>
        {CATEGORIES.map((c) => {
          const count = requestsLoading ? null : (tilesByCategory[c.key] || []).length;
          const selected = c.key === category;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategory(c.key)}
              className={`group rounded-xl border text-left p-3 transition-all hover:-translate-y-0.5 hover:shadow-md ${
                selected
                  ? `border-transparent ring-2 ${c.ring} ${c.tile} shadow-sm`
                  : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${c.tile}`}>
                  <span className="text-xl" aria-hidden="true">{c.emoji}</span>
                </div>
                <div className="min-w-0">
                  <p className={`text-2xl font-bold leading-none ${count === 0 ? 'text-gray-300 dark:text-gray-600' : c.text}`}>
                    {count === null ? '…' : count}
                  </p>
                  <p className="mt-1 text-xs font-semibold text-gray-600 dark:text-gray-300 truncate">{c.label}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Tiles for the selected service line */}
      <div className="flex-1 min-h-[220px]">
        {loading ? (
          <div className="h-full flex items-center justify-center py-10">
            <BBALoader size={32} />
          </div>
        ) : tiles.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-10">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 ${activeCategory.tile}`}>
              <Megaphone className={`w-7 h-7 ${activeCategory.text}`} />
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{activeCategory.empty}</p>
          </div>
        ) : (
          <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-2 xl:grid-cols-3'}`}>
            {tiles.slice(0, 9).map((tile) => (
              <button
                key={tile.id}
                type="button"
                onClick={tile.onClick}
                className="group flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 text-left transition-all hover:shadow-md hover:-translate-y-0.5"
              >
                <ContactWithLogo account={tile.account} company={tile.company} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{tile.company}</p>
                  {tile.subtitle && (
                    <p className="text-xs text-gray-600 dark:text-gray-300 truncate">{tile.subtitle}</p>
                  )}
                  {tile.meta && (
                    <p className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                      <span className={`w-1.5 h-1.5 rounded-full ${activeCategory.dot}`} />
                      {tile.meta}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors shrink-0" />
              </button>
            ))}
          </div>
        )}
        {!loading && tiles.length > 9 && (
          <p className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
            +{tiles.length - 9} more in {activeCategory.label}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => navigate('/crm/fulfillment/marketing', { state: { activeTab: category } })}
        className="mt-5 w-full py-2 px-4 rounded-lg text-sm font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
      >
        Open Marketing Board
      </button>
    </div>
  );
};

export default MarketingFulfillmentWidgets;
